import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import styles from "../App.module.css";
import { APP_STORE_URL, MarketingFooter, MarketingNav } from "../Components/MarketingSite";
import { trackEvent } from "../analytics";

const steps = [
  [
    "Open a web page in Safari.",
    "Any article works. Do this on the page you want to read, listen to, or summarize.",
  ],
  [
    "Open Safari's page menu.",
    "Tap the icon at the left of the address bar. On an article that Safari can show in Reader, that icon is the Reader button: press and hold it to open the menu.",
  ],
  [
    "Choose Manage Extensions and turn on Creata AI.",
    "You only do this once. You can also turn it on in the iPhone Settings app: Apps, then Safari, then Extensions, then Creata AI (on iOS 17, Safari, then Extensions).",
  ],
  [
    "Allow it for the website.",
    "Safari asks whether Creata AI may use the site. Choose Always Allow for sites you trust, or Allow for One Day. Safari asks per website, so it asks again on a new site.",
  ],
  [
    "Tap the round C button.",
    "It appears on the page. You can drag it to the edge of the screen so it stays out of the way. Tap it to open the panel.",
  ],
  [
    "Choose Send this page.",
    "If you selected some text first, you can also choose Send selected text. Creata AI opens and shows what it can make from the page: listen, a summary, questions, a podcast, an MP3, or a song.",
  ],
];

const screens = [
  ["1-page-menu", "Step 2: open Safari's page menu. Manage Extensions and Creata are in it."],
  ["2-manage-extensions", "Step 3: turn on Creata in Manage Extensions."],
  ["3-allow-site", "Step 4: allow Creata for the website. Choose Allow for One Day or Always Allow."],
  ["4-send-this-page", "Steps 5 and 6: tap the round C button, then Send this page."],
];

const fixes = [
  [
    "I do not see the C button.",
    "Check that the extension is on in Manage Extensions, and that you chose Allow for this website. The button only appears on sites you allowed. Reload the page after changing the setting.",
  ],
  [
    "I cannot find Manage Extensions.",
    "On an article, the left icon in the address bar can be the Reader button. Press and hold it to see the page menu. You can also turn the extension on from iPhone Settings, Apps, Safari, Extensions.",
  ],
  [
    "It does not work in a Private tab.",
    "Safari keeps extensions off in Private Browsing unless you allow them. In Settings, Apps, Safari, Extensions, open Creata AI and turn on Allow in Private Browsing.",
  ],
  [
    "The page behind a login or paywall.",
    "The button sends the text you can see on the page, so it works with pages that only your browser can open. If a page loads its text late, wait for it to appear before you tap Send this page.",
  ],
  [
    "Apple News stories.",
    "Safari is not involved. Share the story to Creata AI, or take screenshots of it and choose to read it from the screenshots.",
  ],
];

const SafariGuide = () => {
  const theme = createTheme({ palette: { mode: "dark" } });
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <main className={styles.landingPage}>
        <MarketingNav />

        <section className={styles.productSection} aria-labelledby="safari-title">
          <div className={styles.productVisuals}>
            <figure className={styles.promptScreen}>
              <img src="/img/store/03_send_from_safari.jpg" alt="The Creata AI button in Safari offering to send the page you are reading" />
            </figure>
          </div>
          <div className={styles.productCopy}>
            <p className={styles.eyebrow}><AutoAwesomeIcon fontSize="small" /> Safari button</p>
            <h1 id="safari-title">Send any page to Creata AI from Safari.</h1>
            <p>
              Read the page you are on, listen to it, or turn it into a summary, a podcast, or a song. It also works on pages behind a login. Turn it on once, then use it on any site you allow.
            </p>
            <ol className={styles.stepsList}>
              {steps.map(([title, copy]) => (
                <li key={title}><strong>{title}</strong><span>{copy}</span></li>
              ))}
            </ol>
          </div>
        </section>

        <section className={styles.portalSection} aria-labelledby="see-title">
          <div className={styles.sectionIntro}>
            <p className={styles.eyebrow}>What you will see</p>
            <h2 id="see-title">The screens, in order.</h2>
          </div>
          <div className={styles.galleryStrip}>
            {screens.map(([file, caption]) => (
              <figure className={styles.galleryItem} key={file}>
                <img src={`/img/safari/${file}.jpg`} alt={caption} loading="lazy" />
                <figcaption className={styles.galleryCaption}>{caption}</figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className={styles.supportFaqSection} aria-labelledby="fix-title">
          <div className={styles.sectionIntro}>
            <p className={styles.eyebrow}>Troubleshooting</p>
            <h2 id="fix-title">If it does not work.</h2>
          </div>
          <div className={styles.supportFaqList}>
            {fixes.map(([title, copy]) => (
              <article className={styles.supportFaqItem} key={title}>
                <h3>{title}</h3>
                <p>{copy}</p>
              </article>
            ))}
          </div>
          <div className={styles.heroActions}>
            <a className={styles.textCta} href="/support" onClick={() => trackEvent("support_link_click", { destination: "safari_guide_support" })}>
              More help <ArrowForwardIcon fontSize="small" />
            </a>
            <a className={styles.primaryCta} href={APP_STORE_URL} target="_blank" rel="noreferrer" onClick={() => trackEvent("app_store_click", { app_name: "Creata AI", placement: "safari_guide" })}>
              Download for iPhone <ArrowForwardIcon fontSize="small" />
            </a>
          </div>
        </section>

        <MarketingFooter />
      </main>
    </ThemeProvider>
  );
};

export default SafariGuide;
